import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import dashboardService from '../../services/dashboardService';
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiAlertCircle, FiCheckCircle, FiFolder } from 'react-icons/fi';
import { CURRENCY_SYMBOL } from '../../config/constants';
import { FaFileExcel } from 'react-icons/fa';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const FinancialDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const Inr = CURRENCY_SYMBOL

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await dashboardService.getFinancialDashboard();
      setData(result);
    } catch (error) {
      console.error('Error loading financial dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading financial dashboard...</div>
      </div>
    );
  }

  const budgetStatusData = {
    labels: ['Under Budget', 'On Budget', 'Over Budget'],
    datasets: [{
      data: [
        data?.budgetStatus?.underBudget || 0,
        data?.budgetStatus?.onBudget || 0,
        data?.budgetStatus?.overBudget || 0
      ],
      backgroundColor: ['#10B981', '#3B82F6', '#EF4444'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const projectBudgetData = {
    labels: data?.projects?.slice(0, 10).map(p => p.name.substring(0, 20)) || [],
    datasets: [
      {
        label: 'Budget',
        data: data?.projects?.slice(0, 10).map(p => p.budget) || [],
        backgroundColor: '#3B82F6',
        borderRadius: 8,
      },
      {
        label: 'Spent',
        data: data?.projects?.slice(0, 10).map(p => p.spent) || [],
        backgroundColor: '#EF4444',
        borderRadius: 8,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 12 }
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`
        }
      }
    }
  };

  const getBudgetStatusColor = (status) => {
    const colors = {
      'Under Budget': 'bg-green-100 text-green-800',
      'On Budget': 'bg-blue-100 text-blue-800',
      'Over Budget': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getProjectStatusColor = (status) => {
    const colors = {
      'Planning': 'bg-gray-100 text-gray-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'On Hold': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleDownloadFinancialList = () => {
    const projects = data?.projects || [];
    if (projects.length === 0) {
      return;
    }

    const headers = [
      'Project', 'Status', 'Budget', 'Spent', 'Remaining', 'Utilization', 'Budget Status'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = projects.map((p) => [
      p.name,
      p.projectStatus,
      p.budget,
      p.spent,
      p.remaining,
      `${p.utilization}%`,
      p.budgetStatus,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `financial-dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Dashboard</h1>
          <p className="text-gray-600 mt-1">Budget tracking and financial performance metrics</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Budget</p>
              <p className="text-3xl font-bold text-gray-900 mt-2 d-flex">
                <span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} > <Inr /></span> {(data?.summary?.totalBudget || 0).toLocaleString()}
              </p>
            </div>
            <Inr className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Spent</p>
              <p className="text-3xl font-bold text-gray-900 mt-2 d-flex">
                <span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} > <Inr /></span>{(data?.summary?.totalSpent || 0).toLocaleString()}
              </p>
            </div>
            <FiTrendingDown className="w-12 h-12 text-red-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Remaining</p>
              <p className="text-3xl font-bold text-gray-900 mt-2 d-flex">
                <span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} > <Inr /></span>{(data?.summary?.totalRemaining || 0).toLocaleString()}
              </p>
            </div>
            <FiTrendingUp className="w-12 h-12 text-green-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Utilization</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {data?.summary?.overallUtilization || 0}%
              </p>
            </div>
            <FiFolder className="w-12 h-12 text-purple-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Budget Status Overview */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Budget Status Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg custom-bg-green">
            <FiCheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Under Budget</p>
              <p className="text-2xl font-bold text-green-600">{data?.budgetStatus?.underBudget || 0}</p>
              <p className="text-xs text-gray-500">projects</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
            <FiCheckCircle className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">On Budget</p>
              <p className="text-2xl font-bold text-blue-600">{data?.budgetStatus?.onBudget || 0}</p>
              <p className="text-xs text-gray-500">projects</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-red-50 rounded-lg custom-bg-red">
            <FiAlertCircle className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-600">Over Budget</p>
              <p className="text-2xl font-bold text-red-600">{data?.budgetStatus?.overBudget || 0}</p>
              <p className="text-xs text-gray-500">projects</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget Status Distribution */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Budget Status Distribution</h2>
          <div className="h-80">
            <Doughnut data={budgetStatusData} options={chartOptions} />
          </div>
        </div>

        {/* Top Projects by Budget */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Top 10 Projects - Budget vs Spent</h2>
          <div className="h-80">
            <Bar data={projectBudgetData} options={barChartOptions} />
          </div>
        </div>
      </div>

      {/* Project Financial Details */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Project Financial Details</h2>
          <button
            onClick={handleDownloadFinancialList}
              className="px-4 py-3 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
            title="Download financial report"
          >
            <FaFileExcel />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Budget
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remaining
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Budget Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.projects?.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No projects found
                  </td>
                </tr>
              ) : (
                data?.projects?.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiFolder className="w-5 h-5 text-blue-500 mr-2" />
                        <div className="text-sm font-medium text-gray-900">{project.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getProjectStatusColor(project.projectStatus)}`}>
                        {project.projectStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 d-flex"><span className="fs-2" > <Inr /></span>{project.budget.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 d-flex"><span className="fs-2"  > <Inr /></span> {project.spent.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium d-flex ${project.remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="fs-2"> <Inr /></span>{Math.abs(project.remaining).toLocaleString()}
                        {project.remaining < 0 && ' (over)'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${project.utilization > 100 ? 'bg-red-600' :
                              project.utilization >= 90 ? 'bg-yellow-600' :
                                'bg-green-600'
                              }`}
                            style={{ width: `${Math.min(project.utilization, 100)}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${project.utilization > 100 ? 'text-red-600' :
                          project.utilization >= 90 ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                          {project.utilization}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 custom-bg-green inline-flex text-xs leading-5 font-semibold rounded-full ${getBudgetStatusColor(project.budgetStatus)}`}>
                        {project.budgetStatus}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Financial Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600 ">Total Projects</span>
              <span className="text-lg font-bold text-gray-900">{data?.summary?.projectCount || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg custom-bg-green">
              <span className="text-sm text-gray-600 ">Under Budget</span>
              <span className="text-lg font-bold text-green-600 ">
                {data?.budgetStatus?.underBudget || 0} ({data?.summary?.projectCount > 0 ? Math.round((data?.budgetStatus?.underBudget / data?.summary?.projectCount) * 100) : 0}%)
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg custom-bg-red">
              <span className="text-sm text-gray-600">Over Budget</span>
              <span className="text-lg font-bold text-red-600">
                {data?.budgetStatus?.overBudget || 0} ({data?.summary?.projectCount > 0 ? Math.round((data?.budgetStatus?.overBudget / data?.summary?.projectCount) * 100) : 0}%)
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Health</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-gray-600">Average Utilization</span>
              <span className="text-lg font-bold text-blue-600">{data?.summary?.overallUtilization || 0}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg custom-bg-purple">
              <span className="text-sm text-gray-600">Budget Efficiency</span>
              <span className="text-lg font-bold text-purple-600">
                {data?.summary?.projectCount > 0 && data?.budgetStatus?.underBudget > 0 ? 'Good' : 'Needs Attention'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg custom-bg-orange">
              <span className="text-sm text-gray-600">Cost Control</span>
              <span className="text-lg font-bold text-orange-600 custom-orange-text">
                {data?.budgetStatus?.overBudget === 0 ? 'Excellent' : 'Monitor'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;

